import { useRef, useCallback } from 'react';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useConnectedProjects } from '@atlassian/jira-shared-software-dates';
import { fetchProjectAvatar } from '../../../services/project-avatar';
import {
	getIsProjectMigratedProperty,
	getProjectProperty,
	type ProjectPropertiesApiResponse,
	setIsProjectMigratedProperty,
} from '../../../services/project-properties';
import type { ConnectedProject } from '../../../types';
import { migrateConnectedProjectToAgsExperience } from './experiences';

export const useConnectedProjectMigrationToAgs = (projectId: string) => {
	const project = useProject();
	const isProjectAdmin = project.permissions.administerProject;
	const { onProjectConnect: migrateConnectedProjectToAgs } = useConnectedProjects(projectId);
	/**
	 * Since value of "is migrated" project property will not change during the session after migration to AGS,
	 * this ref prevents unnecessary subsequent network requests after each change of the connected projects picker
	 * */
	const isMigratedFromRestToAgs = useRef<boolean>(false);

	const migrateConnectedProjectPropertyIfNecessary = useCallback(async (): Promise<
		ConnectedProject | undefined
	> => {
		if (isMigratedFromRestToAgs.current) {
			return;
		}

		let isMigratedFromRestToAgsProjectProperty: ProjectPropertiesApiResponse<boolean>;

		migrateConnectedProjectToAgsExperience.start();
		try {
			isMigratedFromRestToAgsProjectProperty = await getIsProjectMigratedProperty<boolean>({
				projectKeyOrId: projectId,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			migrateConnectedProjectToAgsExperience.failure({
				metadata: {
					location: 'Failed to fetch "is migrated" connected project property for admin user',
					error: err?.message,
				},
			});
			throw err;
		}

		if (isMigratedFromRestToAgsProjectProperty.value) {
			migrateConnectedProjectToAgsExperience.abort();
			isMigratedFromRestToAgs.current = true;
			return;
		}

		let connectedProjectProperty: ProjectPropertiesApiResponse<ConnectedProject>;

		try {
			connectedProjectProperty = await getProjectProperty<ConnectedProject>({
				projectKeyOrId: projectId,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			migrateConnectedProjectToAgsExperience.failure({
				metadata: {
					location: 'Failed to fetch connected project via REST before migrating to AGS',
					error: err?.message,
				},
			});
			throw err;
		}

		const { value: connectedProjectRest } = connectedProjectProperty;

		if (!connectedProjectRest) {
			try {
				await setIsProjectMigratedProperty<boolean>({
					projectKeyOrId: projectId,
					propertyValue: true,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				fireErrorAnalytics({
					meta: {
						id: 'setIsProjectMigratedProperty',
						packageName: 'jiraBusinessCalendar',
						teamName: 'wanjel',
					},
					error: err,
					sendToPrivacyUnsafeSplunk: true,
					attributes: {
						projectId,
						location:
							'Failed to mark connected project as migrated to AGS when migration is not needed',
					},
				});
				// we intentionally do not throw error here, as migration, was not needed and we only failed to mark it
				// this error shouldn't prevent the UI from being interactive
			}

			migrateConnectedProjectToAgsExperience.success();
			isMigratedFromRestToAgs.current = true;
			return;
		}

		let connectedProjectAvatarUrl = '';
		try {
			connectedProjectAvatarUrl = await fetchProjectAvatar(connectedProjectRest.id);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchProjectAvatar',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
				attributes: { projectId },
			});
			// we intentionally do not throw error here, as we can still proceed with migration
		}

		try {
			await migrateConnectedProjectToAgs(
				{
					id: connectedProjectRest.id,
					name: connectedProjectRest.name,
					avatarUrl: connectedProjectAvatarUrl,
				},
				{ showSuccessFlag: false, fireAnalyticsOnSuccess: false },
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			migrateConnectedProjectToAgsExperience.failure({
				metadata: {
					location: 'Failed to migrate connected project from project properties to AGS',
					error: err?.message,
				},
			});
			throw err;
		}

		try {
			await setIsProjectMigratedProperty<boolean>({
				projectKeyOrId: projectId,
				propertyValue: true,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireErrorAnalytics({
				meta: {
					id: 'setIsProjectMigratedProperty',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
				attributes: {
					projectId,
					location: 'Failed to mark connected project as migrated to AGS after migration complete',
				},
			});
			// we intentionally do not throw error here, as migration, in fact has completed, so we failed to mark it
			// this error shouldn't prevent the UI from being interactive
		}

		migrateConnectedProjectToAgsExperience.success();
		isMigratedFromRestToAgs.current = true;

		return {
			id: connectedProjectRest.id,
			name: connectedProjectRest.name,
			avatarUrl: connectedProjectAvatarUrl,
			resourceId: connectedProjectRest.resourceId,
		};
	}, [migrateConnectedProjectToAgs, projectId]);

	const shouldFetchFromRestWithoutMigration = useCallback(async () => {
		if (isMigratedFromRestToAgs.current || isProjectAdmin) {
			return false;
		}

		let isMigratedFromRestToAgsProjectProperty: ProjectPropertiesApiResponse<boolean>;

		try {
			isMigratedFromRestToAgsProjectProperty = await getIsProjectMigratedProperty<boolean>({
				projectKeyOrId: projectId,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireErrorAnalytics({
				meta: {
					id: 'getIsProjectMigratedProperty',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
				attributes: {
					projectId,
					location: 'Failed to fetch "is migrated" connected project property for non-admin user',
				},
			});
			throw err;
		}

		if (isMigratedFromRestToAgsProjectProperty.value) {
			isMigratedFromRestToAgs.current = true;
			return false;
		}

		return true;
	}, [isProjectAdmin, projectId]);

	const fetchConnectedProjectFromRest = useCallback(async () => {
		let connectedProjectProperty: ProjectPropertiesApiResponse<ConnectedProject>;

		try {
			connectedProjectProperty = await getProjectProperty<ConnectedProject>({
				projectKeyOrId: projectId,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireErrorAnalytics({
				meta: {
					id: 'getProjectProperty',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
				attributes: { projectId, location: 'Failed to fetch connected project via REST' },
			});
			throw err;
		}

		if (!connectedProjectProperty.value) {
			return;
		}

		const { value: connectedProjectRest } = connectedProjectProperty;

		let connectedProjectAvatarUrl = '';
		try {
			connectedProjectAvatarUrl = await fetchProjectAvatar(connectedProjectRest.id);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchProjectAvatar',
					packageName: 'jiraBusinessCalendar',
					teamName: 'wanjel',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
				attributes: { projectId },
			});
			// we intentionally do not throw error here, as this error is not critical for the UI
		}

		return {
			id: connectedProjectRest.id,
			name: connectedProjectRest.name,
			avatarUrl: connectedProjectAvatarUrl,
			resourceId: connectedProjectRest.resourceId,
		};
	}, [projectId]);

	return {
		shouldFetchFromRestWithoutMigration,
		migrateConnectedProjectPropertyIfNecessary,
		fetchConnectedProjectFromRest,
	};
};
